import { useEffect, useState, useCallback } from "react";
import { Box, Flex, useToast, Input, InputGroup, InputLeftElement } from "@chakra-ui/react";
import { inventoryService } from "../../../service/inventory/inventory.service";
import { InventoryBulkFilter } from "../../../models/filter/inventory-bulk.filter";
import { InventoryModel } from "../../../models/inventory.model";
import {
  updateFilter as inventoryUpdate,
} from "../../../store/slice/Account/inventory.slice";
import { useAppDispatch, useAppSelector } from "../../../util/hooks.util";
import { downloadCsvToMachine } from "../../../util/file.util";
import { ButtonCustom } from "../../../components/form/controls/form.button";
import { InventoryTable } from "../../../components/app/inventory/inventory.table";
import { SearchIcon } from "@chakra-ui/icons";
import { useSearchParams } from "react-router-dom";
import { kandjiService } from "../../../service/integrations/kandji.service";
import { errorToast, successToast } from "../../../constants/toast.constants";
import { BadgeDollarSignIcon, RefreshCw } from "lucide-react";
import { RequestQuoteModal } from "../../../components/app/inventory/request-quote.modal";
import ConditionalTooltip from "../../../components/shared/ConditionalTooltip";

const PAGE = 1;
const PAGE_SIZE = 1000;
const MIN_ITEMS_FOR_QUOTE = 10;

export const Inventory = ({}) => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const { filter, sortColumn, pageSize } = useAppSelector(
    (state) => state.inventory
  );

  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [requestQuoteModalOpen, setRequestQuoteModalOpen] = useState(false);

  const [inventory, setInventory] = useState({
    data: [],
    totalItems: 0,
    totalPage: 0,
  } as { data: InventoryModel[]; totalPage: number; totalItems: number });

  const [inventoryData, setInventoryData] = useState([] as InventoryModel[]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [pageNum, setPageNum] = useState(1);
  const [checkedIds, setCheckedIds] = useState([] as string[]);
  const [showComplete, setShowComplete] = useState(false);
  const [pageWidth, setPageWidth] = useState("100%");
  const [searchParams] = useSearchParams();

  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [activeInventory, setActiveInventory] = useState(null);
  const [searchParamReady, setSearchParamReady] = useState(false);

  const [canRequestQuote, setCanRequestQuote] = useState(false);

  useEffect(() => {
    if (checkedIds?.length >= MIN_ITEMS_FOR_QUOTE && !loading) {
      setCanRequestQuote(true);
    } else {
      setCanRequestQuote(false);
    }
  }, [checkedIds, loading]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fuzzy search function
  const fuzzySearch = useCallback((items: InventoryModel[], searchTerm: string): InventoryModel[] => {
    if (!searchTerm.trim()) return items;

    const searchLower = searchTerm.toLowerCase();
    
    return items.filter(item => {
      const make = item.make?.toLowerCase() || '';
      const model = item.model?.toLowerCase() || '';
      const serial = item.serialNumber?.toLowerCase() || '';
      const processor = item.processor?.toLowerCase() || '';
      
      // Check if any field contains the search term
      return make.includes(searchLower) || 
             model.includes(searchLower) || 
             serial.includes(searchLower) || 
             processor.includes(searchLower);
    });
  }, []);

  // Apply search filter when debounced search term changes
  useEffect(() => {
    if (inventoryData.length > 0) {
      const filteredData = fuzzySearch(inventoryData, debouncedSearchTerm);
      setInventory(prev => ({
        ...prev,
        data: filteredData
      }));
    }
  }, [debouncedSearchTerm, inventoryData, fuzzySearch]);

  useEffect(() => {
    if (searchParams.get("inventory_id")) {
      setActiveInventory(searchParams.get("inventory_id"));
    }
    if (searchParams.get("searchTerm")) {
      setGlobalSearchTerm(searchParams.get("searchTerm"));
    }
    if (searchParams.get("showComplete")) {
      setShowComplete(searchParams.get("showComplete") === "true");
    }

    setSearchParamReady(true);
  }, [searchParams]);

  const init = async (page?: number) => {
    setLoading(true);
    const filterPayload = {
      ...(filter ?? {}),
      source: "KANDJI",
      searchTerm: globalSearchTerm,
    };

    await inventoryService
      .getList(filterPayload, undefined, PAGE, PAGE_SIZE, sortColumn)
      .then((inventory) => {
        setInventory(inventory);
        setInventoryData(inventory.data); // Store original data for client-side search
      })
      .finally(() => setLoading(false));
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setLoading(true);

    toast(successToast("Syncing started. This may take a few minutes, so we will email you when it's done."));
    await kandjiService
      .syncDevices()
      .then((resp) => {
        init();
        toast(successToast("Devices synced successfully"));
      })
      .catch((err) => {
        toast(errorToast("Failed to sync devices"));
      })
      .finally(() => {
        setIsSyncing(false);
        setLoading(false);
      });
  };

  const handleExport = async () => {
    setLoading(true);

    const filterPayload = {
      ...(filter ?? {}),
      source: "KANDJI",
      searchTerm: globalSearchTerm,
    };

    await inventoryService
      .exportToCsv({
        ids: !!checkedIds?.length ? checkedIds : undefined,
        ...filterPayload,
      })
      .then((csvData) => {
        downloadCsvToMachine(csvData, "inventory-export.csv");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (searchParamReady) {
      init();
    }
  }, [globalSearchTerm, searchParamReady, filter, sortColumn, pageSize]);

  return (
    <>
      <Flex
        w={pageWidth}
        flexDir="row"
        justifyContent="space-between"
        alignItems="center"
        gap="8px"
        my={{ base: "0px", md: "16px" }}
      >
        <h1>Ready for Disposal</h1>

        <InputGroup maxW="400px" flex="1" mx="16px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search by make, model, serial, or processor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="white"
            border="1px solid #E2E8F0"
            _focus={{
              borderColor: "brand.green",
              boxShadow: "0 0 0 1px var(--chakra-colors-brand-green)"
            }}
          />
        </InputGroup>

        <Flex flexDir="row" gap="6px" justifyContent="flex-end">
          <ConditionalTooltip
            enabled={!canRequestQuote}
            placement="bottom"
            label="You need at least 10 devices to request a rebate quote and process a collection"
            forceWrapChildren
         >
            <ButtonCustom
              disabled={!canRequestQuote}
              onClick={() => setRequestQuoteModalOpen(true)}
            >
              <Box mr="6px">Request Rebate Quote</Box>
              <BadgeDollarSignIcon size={16} />
            </ButtonCustom>
          </ConditionalTooltip>
          {/* <ButtonCustom
            disabled={!inventory.data?.length || loading}
            neutral
            onClick={handleExport}
          >
            Export
            <DownloadIcon ml="6px" />
          </ButtonCustom> */}
          <ButtonCustom
            neutral
            onClick={handleSync}
            disabled={isSyncing || loading}
          >
            <Box mr="6px">{isSyncing ? "Syncing..." : "Sync Devices"}</Box>
            <RefreshCw size={16} />
          </ButtonCustom>
        </Flex>
      </Flex>

      <InventoryTable
        disableFilter
        excludeColumns={["jobId", "status", "eraseMethod", "rebate", "notes"]}
        key={inventory.data.length}
        inventory={inventory.data}
        loading={loading && !isSyncing}
        onDelete={(id: string) =>
          setInventory({
            data: inventory.data.filter((i) => i.id !== id),
            totalPage: inventory.totalPage,
            totalItems: inventory.totalItems,
          })
        }
        onEdit={init}
        onCheckChange={(data) =>
          setCheckedIds(data.filter((x) => x.isChecked).map((x) => x.id))
        }
        onFilterChange={(filter: InventoryBulkFilter) => {
          setPageNum(1);
          dispatch(inventoryUpdate(filter));
        }}
        onPageWidthChange={(width) => setPageWidth(width)}
        onSort={() => setPageNum(1)}
        // footer={
        //   <TableFooter
        //     count={inventory.data?.length}
        //     totalItems={inventory.totalItems}
        //     totalPage={inventory.totalPage}
        //     pageNum={pageNum}
        //     onChange={(num) => {
        //       setPageNum(num);
        //       init(num);
        //     }}
        //     pageSize={pageSize ?? 10}
        //     onPageSizeChange={(size) => {
        //       setPageNum(1);
        //       dispatch(updatePageSize({ pageSize: size }));
        //     }}
        //   />
        // }
      />

      {!!checkedIds?.length && (
        <RequestQuoteModal
          isOpen={requestQuoteModalOpen}
          onClose={() => setRequestQuoteModalOpen(false)}
          onSubmit={() => {
            setRequestQuoteModalOpen(false);
            toast(successToast("Quote requested successfully."));
          }}
          inventoryIds={checkedIds}
          totalCount={inventory.data?.length}
        />
      )}
    </>
  );
};
